import { useEffect, useMemo, useState } from 'react';
import { get, post, useAuth } from '../api';
import { Link, useNavigate } from 'react-router-dom';
import TerrainMapPreview from '../components/TerrainMapPreview/TerrainMapPreview';
import { MissionListEntry } from '../types/mission';

interface GameSlot {
  slotId: number;
  user: { id: string; username: string } | null;
}

interface GamePreview {
  terrainMap: { width: number; height: number; data: Record<string, string> };
  ownerOverrides: Record<string, number>;
}

interface Game {
  id: number;
  active: boolean;
  mapId: string | null;
  missionId: string | null;
  currentSlotId: number;
  currentTurn: number;
  wonBy: number[];
  slots: GameSlot[];
  preview: GamePreview | null;
}

const PLAYER_COLORS = ['#68b8e8', '#d43318', '#f7b728', '#5db854'];

function mapIdToName(mapId: string | null): string {
  if (!mapId) return 'Unknown Map';
  return mapId
    .split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function gameStatus(
  game: Game,
  sub: string | undefined
): { label: string; color: string } {
  if (!game.active) {
    if (!game.wonBy || game.wonBy.length === 0) {
      return { label: 'Draw', color: '#888' };
    }
    const myWonSlotIds = game.slots.filter(
      (s) => s.user?.id === sub && game.wonBy.includes(s.slotId)
    );
    return myWonSlotIds.length > 0
      ? { label: 'Won', color: '#23a047' }
      : { label: 'Lost', color: '#e53935' };
  }

  const myCurrentSlot = game.slots.find(
    (s) => s.slotId === game.currentSlotId && s.user?.id === sub
  );
  if (myCurrentSlot) {
    return { label: 'Your turn', color: '#23a047' };
  }

  const currentSlot = game.slots.find((s) => s.slotId === game.currentSlotId);
  const name = currentSlot?.user?.username ?? 'AI';
  return { label: `Waiting for ${name}`, color: '#f5a623' };
}

export default function MyGamesPage() {
  const [games, setGames] = useState<Game[]>([]);
  const [missions, setMissions] = useState<MissionListEntry[]>([]);
  const [missionIndex, setMissionIndex] = useState(0);
  const [restarting, setRestarting] = useState(false);
  const [showRestartConfirm, setShowRestartConfirm] = useState(false);
  const [campaignError, setCampaignError] = useState<string | null>(null);
  const [tab, setTab] = useState<'active' | 'completed'>('active');
  const auth = useAuth();
  const sub = auth?.sub;
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([get('games/active'), get('missions').catch(() => [])])
      .then(([g, m]) => {
        setGames(g as Game[]);
        setMissions(m as MissionListEntry[]);
      })
      .catch(() => {});
  }, []);

  const { activeGames, completedGames } = useMemo(() => {
    // Campaign missions are tracked (and re-started) on the campaign page,
    // not here — hide them from the active list so an in-progress mission
    // doesn't compete with real skirmish games for attention. Completed
    // missions stay visible: they're still the player's history.
    const active = games.filter((g) => g.active && !g.missionId);
    const completed = games.filter((g) => !g.active);

    const sortByTurn = (a: Game, b: Game) => {
      const aMyTurn = a.slots.some(
        (s) => s.slotId === a.currentSlotId && s.user?.id === sub
      );
      const bMyTurn = b.slots.some(
        (s) => s.slotId === b.currentSlotId && s.user?.id === sub
      );
      if (aMyTurn && !bMyTurn) return -1;
      if (!aMyTurn && bMyTurn) return 1;
      return b.currentTurn - a.currentTurn;
    };

    return {
      activeGames: active.sort(sortByTurn),
      completedGames: completed.sort((a, b) => b.id - a.id),
    };
  }, [games, sub]);

  const currentGames = tab === 'active' ? activeGames : completedGames;

  const activeCampaignMission = useMemo(
    () => missions.find((m) => m.activeGame !== null) ?? null,
    [missions]
  );

  const availableMissions = useMemo(
    () => missions.filter((m) => !m.locked && !m.completed),
    [missions]
  );

  useEffect(() => {
    setMissionIndex(0);
  }, [availableMissions.length]);

  const handleContinue = () => {
    if (!activeCampaignMission?.activeGame) return;
    navigate(`/games/${activeCampaignMission.activeGame.id}?from=campaign`);
  };

  const handleStartMission = async (missionId: string) => {
    try {
      const { id: gameId } = await post(`missions/${missionId}/start`, {});
      navigate(`/games/${gameId}?from=campaign`);
    } catch {
      setCampaignError('Could not start mission.');
    }
  };

  const handleRestart = async () => {
    if (!activeCampaignMission?.activeGame) return;
    setRestarting(true);
    setCampaignError(null);
    try {
      await post(`games/${activeCampaignMission.activeGame.id}/surrender`, {});
      const { id: gameId } = await post(
        `missions/${activeCampaignMission.id}/start`,
        {}
      );
      navigate(`/games/${gameId}?from=campaign`);
    } catch {
      setCampaignError('Could not restart mission.');
    } finally {
      setRestarting(false);
      setShowRestartConfirm(false);
    }
  };

  return (
    <div
      style={{
        maxWidth: 900,
        margin: '0 auto',
        paddingBottom: 'calc(88px + env(safe-area-inset-bottom))',
      }}
    >
      <h1 className="title is-3 mb-3">My Games</h1>

      {auth?.isGuest && (
        <div className="notification is-warning is-light mb-5">
          You are playing as a guest. Create an account in order to keep your
          progress.{' '}
          <Link className="button is-warning is-small ml-2" to="/account">
            Create Account
          </Link>
        </div>
      )}

      {missions.length > 0 && (
        <div
          className="box mb-5"
          style={{
            borderRadius: 8,
            borderLeft: '4px solid #caa954',
          }}
        >
          {activeCampaignMission ? (
            <div>
              <div
                className="is-flex is-justify-content-space-between is-align-items-flex-start mb-3"
                style={{ gap: 16, flexWrap: 'wrap' }}
              >
                <div style={{ flex: 1, minWidth: 200 }}>
                  <div
                    className="is-flex is-align-items-center mb-1"
                    style={{ gap: 8 }}
                  >
                    <h3
                      style={{
                        fontSize: '1rem',
                        fontWeight: 700,
                        color: '#caa954',
                      }}
                    >
                      Campaign
                    </h3>
                    <span
                      className="tag is-small"
                      style={{
                        backgroundColor: '#caa954',
                        color: '#1a1a2e',
                        border: 'none',
                        fontWeight: 700,
                      }}
                    >
                      IN PROGRESS
                    </span>
                  </div>
                  <h4
                    style={{
                      fontSize: '1.05rem',
                      fontWeight: 600,
                      wordBreak: 'break-word',
                      lineHeight: 1.3,
                    }}
                  >
                    {activeCampaignMission.name}
                  </h4>
                  <p
                    style={{
                      fontSize: '0.85rem',
                      color: 'var(--text-secondary)',
                      marginTop: 2,
                    }}
                  >
                    {activeCampaignMission.commander.name}
                    {` — ${activeCampaignMission.commander.title}`}
                  </p>
                </div>
                <div className="is-flex" style={{ gap: 8, flexWrap: 'wrap' }}>
                  <button
                    className="button is-primary is-small"
                    onClick={handleContinue}
                  >
                    Continue
                  </button>
                  <button
                    className="button is-light is-small"
                    onClick={() => setShowRestartConfirm(true)}
                  >
                    Restart
                  </button>
                  <Link className="button is-light is-small" to="/campaign">
                    View Campaign
                  </Link>
                </div>
              </div>
              {activeCampaignMission.activeGame?.preview ? (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                >
                  <TerrainMapPreview
                    terrainMap={
                      activeCampaignMission.activeGame.preview.terrainMap
                    }
                    cellSize={5}
                    ownerOverrides={
                      activeCampaignMission.activeGame.preview.ownerOverrides
                    }
                    playerCount={
                      activeCampaignMission.terrainMap
                        ? Object.values(
                            activeCampaignMission.activeGame.preview
                              .ownerOverrides
                          ).filter((v, i, a) => a.indexOf(v) === i).length
                        : 2
                    }
                    highlightOwners={false}
                  />
                </div>
              ) : (
                activeCampaignMission.terrainMap && (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                    }}
                  >
                    <TerrainMapPreview
                      terrainMap={activeCampaignMission.terrainMap}
                      cellSize={5}
                      playerCount={2}
                    />
                  </div>
                )
              )}
              {campaignError && (
                <p className="help is-danger" style={{ marginTop: 8 }}>
                  {campaignError}
                </p>
              )}
            </div>
          ) : availableMissions.length > 0 ? (
            <div>
              <div
                className="is-flex is-justify-content-space-between is-align-items-flex-start mb-3"
                style={{ gap: 16, flexWrap: 'wrap' }}
              >
                <div style={{ flex: 1, minWidth: 200 }}>
                  <h3
                    style={{
                      fontSize: '1rem',
                      fontWeight: 700,
                      color: '#caa954',
                      marginBottom: 4,
                    }}
                  >
                    Campaign
                  </h3>
                  <div
                    className="is-flex is-align-items-center mb-1"
                    style={{ gap: 12 }}
                  >
                    {availableMissions.length > 1 && (
                      <button
                        className="button is-small is-light"
                        onClick={() =>
                          setMissionIndex(
                            (missionIndex - 1 + availableMissions.length) %
                              availableMissions.length
                          )
                        }
                        style={{ minWidth: 'auto', padding: '4px 8px' }}
                      >
                        ◀︎
                      </button>
                    )}
                    <h4
                      style={{
                        fontSize: '1.05rem',
                        fontWeight: 600,
                        wordBreak: 'break-word',
                        lineHeight: 1.3,
                      }}
                    >
                      {availableMissions[missionIndex]?.name}
                    </h4>
                    {availableMissions.length > 1 && (
                      <button
                        className="button is-small is-light"
                        onClick={() =>
                          setMissionIndex(
                            (missionIndex + 1) % availableMissions.length
                          )
                        }
                        style={{ minWidth: 'auto', padding: '4px 8px' }}
                      >
                        ▶︎
                      </button>
                    )}
                  </div>
                  <p
                    style={{
                      fontSize: '0.85rem',
                      color: 'var(--text-secondary)',
                      marginTop: 2,
                    }}
                  >
                    {availableMissions[missionIndex]?.commander.name}
                    {availableMissions[missionIndex]?.commander.title &&
                      ` — ${availableMissions[missionIndex]?.commander.title}`}
                  </p>
                  {availableMissions.length > 1 && (
                    <p
                      style={{
                        fontSize: '0.8rem',
                        color: 'var(--text-muted)',
                        marginTop: 4,
                      }}
                    >
                      Mission {missionIndex + 1} of {availableMissions.length}
                    </p>
                  )}
                </div>
                <div className="is-flex" style={{ gap: 8, flexWrap: 'wrap' }}>
                  <button
                    className="button is-primary is-small"
                    onClick={() =>
                      handleStartMission(availableMissions[missionIndex].id)
                    }
                  >
                    Play
                  </button>
                  <Link className="button is-light is-small" to="/campaign">
                    View Campaign
                  </Link>
                </div>
              </div>
              {availableMissions[missionIndex]?.terrainMap && (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                >
                  <TerrainMapPreview
                    terrainMap={availableMissions[missionIndex].terrainMap}
                    cellSize={5}
                    playerCount={2}
                  />
                </div>
              )}
              {campaignError && (
                <p className="help is-danger" style={{ marginTop: 8 }}>
                  {campaignError}
                </p>
              )}
            </div>
          ) : (
            <div>
              <div
                className="is-flex is-justify-content-space-between is-align-items-center"
                style={{ gap: 16, flexWrap: 'wrap' }}
              >
                <div>
                  <h3
                    style={{
                      fontSize: '1rem',
                      fontWeight: 700,
                      color: '#caa954',
                    }}
                  >
                    Campaign
                  </h3>
                  <p
                    style={{
                      fontSize: '0.95rem',
                      color: 'var(--text-secondary)',
                      marginTop: 2,
                    }}
                  >
                    All missions complete!
                  </p>
                </div>
                <Link className="button is-light is-small" to="/campaign">
                  View Campaign
                </Link>
              </div>
            </div>
          )}
        </div>
      )}

      {(activeGames.length > 0 || completedGames.length > 0) && (
        <div className="tabs mb-5">
          <ul>
            <li className={tab === 'active' ? 'is-active' : ''}>
              <a onClick={() => setTab('active')}>
                Active ({activeGames.length})
              </a>
            </li>
            {completedGames.length > 0 && (
              <li className={tab === 'completed' ? 'is-active' : ''}>
                <a onClick={() => setTab('completed')}>
                  Completed ({completedGames.length})
                </a>
              </li>
            )}
          </ul>
        </div>
      )}

      {currentGames.length === 0 ? (
        <div
          className="mb-5"
          style={{
            color: 'var(--text-muted)',
          }}
        >
          -
        </div>
      ) : (
        <div
          className="mb-5"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: 16,
          }}
        >
          {currentGames.map((game) => {
            const status = gameStatus(game, sub);
            const playerCount = game.slots.length;

            return (
              <div
                key={game.id}
                className="box"
                style={{
                  borderRadius: 8,
                  padding: '16px 16px 14px',
                  borderLeft: `4px solid ${status.color}`,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <div
                  className="is-flex is-justify-content-space-between is-align-items-flex-start mb-3"
                  style={{ gap: 8 }}
                >
                  <h3
                    style={{
                      fontSize: '1rem',
                      fontWeight: 600,
                      wordBreak: 'break-word',
                      lineHeight: 1.3,
                    }}
                  >
                    {game.mapId
                      ? mapIdToName(game.mapId)
                      : game.missionId ?? 'Unknown Map'}
                  </h3>
                  <span
                    className="tag is-small"
                    style={{
                      backgroundColor: status.color,
                      color: '#fff',
                      border: 'none',
                      flexShrink: 0,
                    }}
                  >
                    {status.label}
                  </span>
                </div>

                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '4px 14px',
                    marginBottom: 10,
                  }}
                >
                  {game.slots.map((slot) => {
                    const color =
                      PLAYER_COLORS[slot.slotId % PLAYER_COLORS.length];
                    const isMe = slot.user?.id === sub;
                    const label = slot.user?.username ?? 'AI';
                    return (
                      <div
                        key={slot.slotId}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 5,
                          fontSize: '0.85rem',
                        }}
                      >
                        <span
                          style={{
                            width: 9,
                            height: 9,
                            borderRadius: '50%',
                            backgroundColor: color,
                            flexShrink: 0,
                          }}
                        />
                        <span
                          style={{
                            color: isMe
                              ? 'var(--text-primary)'
                              : 'var(--text-muted)',
                            fontWeight: isMe ? 600 : 400,
                          }}
                        >
                          {label}
                        </span>
                      </div>
                    );
                  })}
                </div>

                {game.preview ? (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      marginBottom: 12,
                    }}
                  >
                    <TerrainMapPreview
                      terrainMap={game.preview.terrainMap}
                      cellSize={4}
                      ownerOverrides={game.preview.ownerOverrides}
                      playerCount={playerCount}
                      highlightOwners={false}
                    />
                  </div>
                ) : (
                  <div
                    style={{
                      marginBottom: 12,
                      padding: '12px 0',
                      textAlign: 'center',
                      fontSize: '0.8rem',
                      color: 'var(--text-muted)',
                      fontStyle: 'italic',
                    }}
                  >
                    Not started yet
                  </div>
                )}

                <div
                  className="is-flex is-justify-content-space-between is-align-items-center"
                  style={{ marginTop: 'auto', gap: 8 }}
                >
                  <span
                    style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}
                  >
                    Turn {game.currentTurn + 1}
                  </span>
                  <div className="is-flex" style={{ gap: 6 }}>
                    {!game.active && (
                      <Link
                        className="button is-small is-info is-light"
                        to={`/games/${game.id}?spectate=true`}
                      >
                        Replay
                      </Link>
                    )}
                    <Link
                      className={`button is-small ${
                        status.label === 'Your turn' ||
                        (game.active && status.label.startsWith('Waiting'))
                          ? 'is-primary'
                          : 'is-light'
                      }`}
                      to={`/games/${game.id}`}
                    >
                      {game.active ? 'Play' : 'View'}
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div
        style={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 20,
          display: 'flex',
          justifyContent: 'center',
          padding: '12px 16px calc(12px + env(safe-area-inset-bottom))',
          backgroundColor: 'var(--bg-page)',
          borderTop: '1px solid var(--panel-border)',
        }}
      >
        <Link
          className="button is-primary"
          to="/setup"
          style={{ width: '100%', maxWidth: 400 }}
        >
          New Game
        </Link>
      </div>

      <div className={`modal ${showRestartConfirm ? 'is-active' : ''}`}>
        <div
          className="modal-background"
          style={{ backgroundColor: 'rgba(0,0,0,0.55)' }}
          onClick={() => setShowRestartConfirm(false)}
        />
        <div
          style={{
            position: 'relative',
            backgroundColor: 'var(--panel-bg)',
            color: 'var(--text-primary)',
            border: '1px solid rgba(68,68,102,0.6)',
            borderRadius: 10,
            maxWidth: 400,
            width: '100%',
            boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
            margin: '0 16px',
          }}
        >
          <div
            style={{
              padding: '20px 24px 12px',
              borderBottom: '1px solid rgba(68,68,102,0.35)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <h2
              className="title is-5 mb-0"
              style={{ color: 'var(--text-primary)' }}
            >
              Restart Mission
            </h2>
            <button
              className="delete is-medium"
              aria-label="close"
              onClick={() => setShowRestartConfirm(false)}
            />
          </div>
          <div style={{ padding: '20px 24px' }}>
            <p style={{ color: 'var(--text-secondary)' }}>
              Your current progress will be lost. Restart this mission?
            </p>
            {campaignError && (
              <p className="help is-danger" style={{ marginTop: 12 }}>
                {campaignError}
              </p>
            )}
          </div>
          <div
            style={{
              padding: '12px 24px 20px',
              borderTop: '1px solid rgba(68,68,102,0.35)',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 8,
            }}
          >
            <button
              className="button is-light"
              onClick={() => {
                setShowRestartConfirm(false);
                setCampaignError(null);
              }}
            >
              Cancel
            </button>
            <button
              className={`button is-primary${restarting ? ' is-loading' : ''}`}
              onClick={handleRestart}
              disabled={restarting}
            >
              Restart
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
